import { type CaseDetailsPage } from '@/cloudFunctions/arag';
import {
  Alert,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  Radio,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { MatterDocument } from '@shared/types';
import { useEffect, useState } from 'react';
import AltMatterAutocomplete from '../AltMatterAutocomplete/AltMatterAutocomplete';
import { formatCurrency } from '@/utils/numberFormatters';
import { type ExistingAragCaseMatch } from './util';
import { AlertTriangleIcon } from 'lucide-react';

const CaseAssistResult = ({
  caseAssistNumber,
  caseAssistResult,
  selectedFeeScheduleIndex,
  onSelectedFeeScheduleIndexChange,
  onShowAddEditMatterModal,
  existingMatches,
  isCheckingExistingMatches,
  existingMatchesError,
  onViewMatter,
}: {
  caseAssistNumber: string;
  caseAssistResult: CaseDetailsPage;
  selectedFeeScheduleIndex: number;
  onSelectedFeeScheduleIndexChange: (value: number) => void;
  onShowAddEditMatterModal: (matter: MatterDocument | undefined) => void;
  existingMatches: ExistingAragCaseMatch[];
  isCheckingExistingMatches: boolean;
  existingMatchesError: string | undefined;
  onViewMatter: (matterId: string) => void;
}) => {
  const [selectedMatter, setSelectedMatter] = useState<
    MatterDocument | undefined
  >();

  const handleAttachToExistingMatter = async () => {
    if (!selectedMatter) {
      notifications.show({
        color: 'red',
        message: 'Please select a matter.',
      });
      return;
    }

    onShowAddEditMatterModal(selectedMatter);
  };

  return (
    <Stack gap='xl'>
      <Stack>
        <Title order={2} size='h3'>
          ARAG Case
        </Title>
        <Paper p='xl' withBorder radius='md'>
          <Stack gap='md'>
            <Stack gap='sm'>
              <Stack gap='xs' style={{ flex: 1 }}>
                <LabelValuePair label='CaseAssist #' value={caseAssistNumber} />
                <LabelValuePair
                  label='Member Name'
                  value={caseAssistResult.memberName ?? '(unknown)'}
                />
                <LabelValuePair
                  label='Member ID'
                  value={caseAssistResult.memberId ?? '(unknown)'}
                />
                <LabelValuePair
                  label='Phone'
                  value={caseAssistResult.memberPhone ?? '(unknown)'}
                />
                <LabelValuePair
                  label='Street Address'
                  value={caseAssistResult.memberStreetAddress ?? '(unknown)'}
                />
                <LabelValuePair
                  label='City/State/Zip'
                  value={caseAssistResult.memberCityStateZip ?? '(unknown)'}
                />
                <LabelValuePair
                  label='Legal Matter'
                  value={caseAssistResult.legalMatter ?? '(unknown)'}
                />
                <LabelValuePair
                  label='Type of Legal Matter'
                  value={caseAssistResult.typeOfLegalMatter ?? '(unknown)'}
                />

                {caseAssistResult.feeSchedule &&
                  caseAssistResult.feeSchedule.length > 0 && (
                    <>
                      <Divider my='xs' />
                      <div>
                        <Text size='sm' fw={600} c='gray.8' mb='xs'>
                          Fee Schedule:
                        </Text>
                        <Radio.Group
                          value={selectedFeeScheduleIndex.toString()}
                          onChange={(value) =>
                            onSelectedFeeScheduleIndexChange(parseInt(value))
                          }
                          ml='md'
                        >
                          <Stack gap='xs'>
                            {caseAssistResult.feeSchedule.map((fee, idx) => (
                              <Radio
                                key={idx}
                                value={idx.toString()}
                                label={
                                  <Text size='sm'>
                                    {fee.description}: {fee.rate}
                                  </Text>
                                }
                              />
                            ))}
                          </Stack>
                        </Radio.Group>
                      </div>
                    </>
                  )}
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <Stack gap='lg' align='center' w='100%'>
        <Stack gap='xs' w='100%'>
          {isCheckingExistingMatches && (
            <Group gap='sm' align='center'>
              <Loader type='dots' size='lg' color='pink.4' />
              <Text size='sm' c='gray.6'>
                Checking for existing matters...
              </Text>
            </Group>
          )}
          {existingMatchesError && (
            <Alert
              color='red'
              radius='md'
              title='Unable to check for existing matters'
              variant='light'
            >
              <Text size='sm'>{existingMatchesError}</Text>
            </Alert>
          )}
          {!existingMatchesError && existingMatches.length > 0 && (
            <Alert
              color='yellow'
              radius='md'
              variant='light'
              icon={<AlertTriangleIcon size={16} />}
            >
              <Stack gap='md'>
                <Stack gap='0'>
                  <Title order={3} size='h4' c='#4D4B47'>
                    Existing matter found
                  </Title>
                  <Text size='sm' c='#4D4B47'>
                    This CaseAssist number already belongs to the following
                    matter
                    {existingMatches.length > 1 ? 's' : ''}:
                  </Text>
                </Stack>
                <Stack gap='sm'>
                  {existingMatches.map((match) => (
                    <Group
                      key={match.matter.id}
                      justify='space-between'
                      align='center'
                    >
                      <Stack gap='0'>
                        <Text size='sm' fw={500} c='#4D4B47'>
                          {match.matter.name}
                        </Text>
                        <Text size='xs' c='#74716B'>
                          Status: {formatStatusLabel(match.matter.status)}
                        </Text>
                        {typeof match.insuranceCase.caseValue === 'number' && (
                          <Text size='xs' c='#74716B'>
                            Case value:{' '}
                            {formatCurrency(match.insuranceCase.caseValue)}
                          </Text>
                        )}
                      </Stack>
                      <Button
                        variant='subtle'
                        size='xs'
                        onClick={() => onViewMatter(match.matter.id)}
                      >
                        Go to matter
                      </Button>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Alert>
          )}
        </Stack>
        <Group wrap='nowrap' w='100%' align='flex-end'>
          <Stack flex='2 0 0'>
            <AltMatterAutocomplete
              size='sm'
              selectedMatterId={selectedMatter?.id}
              where={[['matterType', '==', 'ARAG']]}
              onSelect={(matter) => setSelectedMatter(matter)}
            />

            <Button
              type='button'
              variant='default'
              maw='400px'
              w='100%'
              disabled={selectedMatter === undefined}
              onClick={handleAttachToExistingMatter}
            >
              Add to existing matter
            </Button>
          </Stack>

          <Divider orientation='vertical' labelPosition='center' c='red' />

          <Button
            type='button'
            maw='400px'
            flex='1 0 0'
            onClick={() => {
              onShowAddEditMatterModal(undefined);
            }}
          >
            Create new matter
          </Button>
        </Group>
      </Stack>
    </Stack>
  );
};

export default CaseAssistResult;

const LabelValuePair = ({ label, value }: { label: string; value: string }) => {
  return (
    <Group gap='xs'>
      <Text size='sm' fw={600} c='gray.8'>
        {label}:
      </Text>
      <Text size='sm'>{value}</Text>
    </Group>
  );
};

const formatStatusLabel = (status: string | undefined) => {
  if (!status) {
    return 'Unknown';
  }
  const withSpaces = status.replace(/([A-Z])/g, ' $1');
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};
